import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '../../types/RootState'
import config from '../../../config'
import { Company } from '@leadgen/models/client'
import { imageDimensions } from '../../../constants/imageDimensions'
import { defaultTemplateId } from '../../../constants/defaultTemplateId'

class TemplateSelectors {
  getCompany = (state: RootState) => state.company.company
  getHasCompany = (state: RootState) => !!state.company.company
  getSubscriptionAwaitingSync = (state: RootState) => state.company.subscriptionAwaitingSync
  getPaymentAwaitingSync = (state: RootState) => state.company.paymentAwaitingSync
  getSyncClientSecret = (state: RootState) => state.company.syncClientSecret
  getSyncIntentType = (state: RootState) => state.company.syncIntentType
  getSyncStartTime = createSelector(
    [(state: RootState) => state.company.syncStartTime],
    syncStartTime => {
      return syncStartTime ? new Date(syncStartTime) : null
    },
  )
  getHasActiveSubscription = createSelector([this.getCompany], company => {
    return !!(company?.stripeCustomerId && company?.stripePaymentMethodId)
  })
  getCreditsPurchasedYearMonth = createSelector([this.getCompany], company => {
    return company?.creditsPurchasedYearMonth || null
  })
  getHasHadSubscription = createSelector([this.getCompany], company => {
    return !!company?.creditsPurchasedYearMonth
  })
  getCompanyCreditsPurchasedThisMonth = createSelector([this.getCompany], company => {
    if (!company) {
      return null
    }
    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const currentYearMonth =
      `${currentYear}/${currentMonth > 9 ? currentMonth : `0${currentMonth}`}` as Company['creditsPurchasedYearMonth']
    if (company.creditsPurchasedYearMonth !== currentYearMonth) {
      return 0
    }
    return company.creditsPurchased
  })
  getCompanyCreditsPurchasedMonthlyCreditLimitThisMonth = createSelector(
    [this.getCompany],
    company => {
      if (!company) {
        return null
      }
      const currentYear = new Date().getFullYear()
      const currentMonth = new Date().getMonth() + 1
      const currentYearMonth =
        `${currentYear}/${currentMonth > 9 ? currentMonth : `0${currentMonth}`}` as Company['creditsPurchasedYearMonth']
      if (company.creditsPurchasedYearMonth !== currentYearMonth) {
        return 0
      }
      return company.creditsPurchasedMonthlyCreditLimit
    },
  )
  getCompanyMonthlyCreditLimit = createSelector([this.getCompany], company => {
    return company?.monthlyCreditLimit !== undefined ? company?.monthlyCreditLimit : null
  })
  getCompanyCredits = createSelector([this.getCompany], company => {
    return company?.credits !== undefined ? company?.credits : null
  })
  getCompanyPendingCredits = createSelector(
    [
      this.getCompanyMonthlyCreditLimit,
      this.getCompanyCreditsPurchasedMonthlyCreditLimitThisMonth,
      this.getPaymentAwaitingSync,
      this.getCompanyCredits,
    ],
    (monthlyCreditLimit, creditsPurchasedMonthlyCreditLimit, paymentAwaitingSync, credits) => {
      if (
        paymentAwaitingSync &&
        monthlyCreditLimit !== null &&
        creditsPurchasedMonthlyCreditLimit !== null
      ) {
        const difference = monthlyCreditLimit - creditsPurchasedMonthlyCreditLimit
        const creditsAfterTopup = (credits || 0) + difference
        return creditsAfterTopup
      }
      return credits
    },
  )

  getCompanyId = createSelector([this.getCompany], company => {
    return company?.id || null
  })
  getStripePaymentMethodId = createSelector([this.getCompany], company => {
    return company?.stripePaymentMethodId || null
  })
  getCompanyPlanningCategories = createSelector([this.getCompany], company => {
    return company?.planningCategories || null
  })
  getCompanyPlanningClassifications = createSelector([this.getCompany], company => {
    return company?.planningClassifications || null
  })
  getCompanyPlanningApplicationTypes = createSelector([this.getCompany], company => {
    return company?.planningApplicationTypes || null
  })
  getCompanyPlanningApplicationStatuses = createSelector([this.getCompany], company => {
    return company?.planningApplicationStatuses || null
  })
  getCompanyEstimatedProjectValueBands = createSelector([this.getCompany], company => {
    return company?.estimatedProjectValueBands || null
  })
  getCompanyType = createSelector([this.getCompany], company => {
    return company?.companyType || null
  })
  getLastChangedTemplateVersion = createSelector([this.getCompany], company => {
    return company ? company?.lastChangedTemplateVersion : null
  })
  getCompanyHasTemplate = createSelector(
    [this.getLastChangedTemplateVersion],
    lastChangedTemplateVersion => {
      return lastChangedTemplateVersion !== null
    },
  )
  getCompanyPostcodeDistricts = createSelector([this.getCompany], company => {
    return company?.postcodeDistricts || null
  })
  getPendingReviewTemplateVersion = createSelector([this.getCompany], company => {
    return company?.pendingReviewTemplateVersion !== undefined
      ? company?.pendingReviewTemplateVersion
      : null
  })
  getPendingReviewTemplateFeedback = createSelector([this.getCompany], company => {
    return company?.pendingReviewTemplateFeedback || null
  })
  getCompanyHasPendingReviewTemplate = createSelector(
    [this.getPendingReviewTemplateVersion],
    pendingReviewTemplateVersion => {
      return pendingReviewTemplateVersion !== null
    },
  )
  getDefaultTemplateVersion = createSelector([this.getCompany], company => {
    return company?.defaultTemplateVersion !== undefined ? company?.defaultTemplateVersion : null
  })
  getCompanyHasDefaultTemplate = createSelector(
    [this.getDefaultTemplateVersion],
    defaultTemplateVersion => {
      return defaultTemplateVersion !== null
    },
  )
  getPendingReviewTemplateImageUrl = createSelector(
    [this.getCompanyId, this.getPendingReviewTemplateVersion],
    (companyId, pendingReviewTemplateVersion) => {
      if (pendingReviewTemplateVersion === null) {
        return null
      }
      return `${config.imgixBaseUrl}templates/${companyId}/${defaultTemplateId}_${pendingReviewTemplateVersion}.pdf?page=1&fm=png&auto=format&fit=max&w=${imageDimensions.width}&h=${imageDimensions.height}`
    },
  )
  getDefaultTemplateImageUrl = createSelector(
    [this.getCompanyId, this.getDefaultTemplateVersion],
    (companyId, defaultTemplateVersion) => {
      if (defaultTemplateVersion === null) {
        return null
      }
      return `${config.imgixBaseUrl}templates/${companyId}/${defaultTemplateId}_${defaultTemplateVersion}.pdf?page=1&fm=png&auto=format&fit=max&w=${imageDimensions.width}&h=${imageDimensions.height}`
    },
  )
  getNextMissingOnboarding = createSelector(
    [
      this.getCompanyPostcodeDistricts,
      this.getCompanyType,
      this.getCompanyPlanningCategories,
      this.getCompanyPlanningClassifications,
      this.getCompanyPlanningApplicationTypes,
      this.getCompanyPlanningApplicationStatuses,
      this.getCompanyEstimatedProjectValueBands,
      this.getCompanyHasTemplate,
      this.getHasActiveSubscription,
    ],
    (
      companyPostcodeDistricts,
      companyType,
      companyPlanningCategories,
      companyPlanningClassifications,
      companyPlanningApplicationTypes,
      companyPlanningApplicationStatuses,
      companyEstimatedProjectValueBands,
      hasTemplate,
      hasActiveSubscription,
    ): 'coverage' | 'filters' | 'template' | 'subscribe' | null => {
      const hasPlanningFilters =
        !!companyType &&
        !!companyPlanningCategories?.length &&
        !!companyPlanningClassifications?.length &&
        !!companyPlanningApplicationTypes?.length &&
        !!companyPlanningApplicationStatuses?.length &&
        !!companyEstimatedProjectValueBands?.length
      if (hasActiveSubscription) {
        return null
      }
      if (companyPostcodeDistricts?.length && hasPlanningFilters) {
        return null
      }
      if (hasTemplate) {
        return null
      }
      if (!companyPostcodeDistricts?.length) {
        return 'coverage'
      }
      if (!hasPlanningFilters) {
        return 'filters'
      }
      if (!hasTemplate) {
        return 'template'
      }
      return 'subscribe'
    },
  )
}

export default new TemplateSelectors()
